import type { FC } from 'react';
import { FaCrosshairs, RiArrowRightSLine } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { wikiHref, type WikiT } from '../wikiText';
import { itemTypeName, wikiItemPath, WIKI_ITEM_TYPES, wikiItemText } from '../wikiItemText';
import type { WikiSearchHit } from '../wiki.types';
import type { WikiItemType } from '../wikiItem.types';
import { WikiRemoteImage } from './WikiRemoteImage';
import { WikiName } from './WikiName';
import type { Language } from '../../../i18n/types';

type WikiHitBase = 'skin' | 'weapon' | 'container' | 'collection';

const HIT_PATH: Record<WikiHitBase, string> = {
  skin: '/wiki/skins/',
  weapon: '/wiki/weapons/',
  container: '/wiki/cases/',
  collection: '/wiki/collections/',
};

const HIT_LABEL: Record<WikiHitBase, 'typeSkin' | 'typeWeapon' | 'typeContainer' | 'typeCollection'> = {
  skin: 'typeSkin',
  weapon: 'typeWeapon',
  container: 'typeContainer',
  collection: 'typeCollection',
};

const itemTypeOf = (type: string): WikiItemType | null => WIKI_ITEM_TYPES.find((candidate) => candidate === type) ?? null;

const hitBaseOf = (type: string): WikiHitBase => (type === 'weapon' || type === 'container' || type === 'collection' ? type : 'skin');

// WHY: адрес приходит с бэка — карта типов в адреса живёт в одном месте; у старого бэка его нет, тогда собираем сами
export const wikiHitHref = (lang: Language, hit: WikiSearchHit): string => {
  if (hit.path) return wikiHref(lang, hit.path);
  const itemType = itemTypeOf(hit.type);
  return wikiHref(lang, itemType ? wikiItemPath(itemType, hit.slug) : `${HIT_PATH[hitBaseOf(hit.type)]}${hit.slug}`);
};

const hitLabel = (lang: Language, t: WikiT, type: string): string => {
  const itemType = itemTypeOf(type);
  return itemType ? itemTypeName(wikiItemText(lang), itemType) : t(HIT_LABEL[hitBaseOf(type)]);
};

export const WikiHitRow: FC<{ hit: WikiSearchHit; lang: Language; t: WikiT; active?: boolean; compact?: boolean }> = ({
  hit,
  lang,
  t,
  active = false,
  compact = false,
}) => (
  <span className={cn('flex items-center gap-3 rounded-xl', compact ? 'min-h-[52px] px-2 py-1.5' : 'min-h-[94px] p-2 pe-3', active && 'bg-white/[0.08]')}>
    <span
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/[0.24] text-label-secondary ring-1 ring-inset ring-white/[0.16]',
        compact ? 'size-10' : 'h-[78px] w-[104px] rounded-[14px]',
      )}
      style={hit.rarityColor ? { boxShadow: `inset 0 -3px 0 ${hit.rarityColor}` } : undefined}
    >
      {hit.imageUrl ? (
        <WikiRemoteImage src={hit.imageUrl} alt="" sizes={compact ? '40px' : '104px'} className="p-1" />
      ) : (
        <FaCrosshairs aria-hidden size={compact ? 18 : 24} />
      )}
    </span>
    <span className="flex min-w-0 flex-1 flex-col">
      <span className={cn('text-[15px] font-semibold leading-[20px] text-label-primary', compact ? 'truncate' : '[overflow-wrap:anywhere]')}>
        <WikiName name={hit.name} />
      </span>
      <span className="truncate text-[12px] leading-[16px] text-label-secondary">
        {hitLabel(lang, t, hit.type)}
        {hit.nameEn !== hit.name && <span dir="ltr"> · {hit.nameEn}</span>}
      </span>
    </span>
    {!compact && <RiArrowRightSLine aria-hidden size={20} className="shrink-0 text-label-secondary rtl:-scale-x-100" />}
  </span>
);
